import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Trash2, RefreshCw } from "lucide-react";
import { API } from "@/lib/site";
import { AdminGallery } from "@/pages/AdminGallery";

const SERVICE = { cumpleanos: "Cumpleaños", fiestas: "Fiestas privadas", reservas: "Reservas del local", info: "Información general" };
const STATUSES = ["pendiente", "contactado", "confirmado"];
const STATUS_STYLE = { pendiente: "bg-banana text-jungle", contactado: "bg-flamingo text-deep", confirmado: "bg-palm text-deep" };

const authHeaders = () => ({ withCredentials: true });

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Error";
};

const Login = ({ onOk }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onOk();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-jungle flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-[2rem] bg-paper p-8">
        <img src="/sello-gato-sm.jpg" alt="" className="h-16 w-16 rounded-xl object-cover" />
        <h1 className="font-display text-3xl mt-4">Panel de solicitudes</h1>
        <p className="mt-2 text-sm text-jungle/75">Acceso solo para cuentas autorizadas.</p>
        <input required type="email" autoComplete="username" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" className="mt-6 w-full rounded-xl border border-jungle/20 bg-white/60 px-4 py-3" />
        <input required type="password" autoComplete="current-password" placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" className="mt-3 w-full rounded-xl border border-jungle/20 bg-white/60 px-4 py-3" />
        {error && <p className="mt-3 text-sm text-red-700" data-testid="admin-login-error">{error}</p>}
        <button disabled={busy} data-testid="admin-login-submit" className="btn-shine mt-6 w-full rounded-full bg-mango py-3.5 text-[12px] font-bold uppercase tracking-[0.12em] text-paper">
          <span className="relative z-[2]">{busy ? "Entrando..." : "Entrar"}</span>
        </button>
      </form>
    </div>
  );
};

const Row = ({ b, onStatus, onDelete }) => (
  <article data-testid={`admin-booking-${b.id}`} className="rounded-2xl bg-white/70 border border-jungle/10 p-5 grid grid-cols-1 md:grid-cols-12 gap-4">
    <div className="md:col-span-4">
      <p className="font-display text-xl">{b.name}</p>
      <p className="text-sm text-jungle/75 break-all">{b.email}</p>
      <p className="text-sm text-jungle/75">{b.phone}</p>
    </div>
    <div className="md:col-span-5 text-sm">
      <p><b>{SERVICE[b.service] ?? b.service}</b> · {b.date || "sin fecha"} · {b.guests ?? "—"} pers.</p>
      {b.message && <p className="mt-2 text-jungle/80">{b.message}</p>}
      <p className="mt-2 font-mono-ed text-xs text-jungle/60">{new Date(b.created_at).toLocaleString("es-ES")} · {b.lang?.toUpperCase()}</p>
    </div>
    <div className="md:col-span-3 flex md:flex-col items-start md:items-end gap-2">
      <select value={b.status} onChange={(e) => onStatus(b.id, e.target.value)} data-testid={`admin-status-${b.id}`} className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase ${STATUS_STYLE[b.status]}`}>
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <button onClick={() => onDelete(b.id)} data-testid={`admin-delete-${b.id}`} className="inline-flex items-center gap-1 text-xs text-red-700 hover:underline"><Trash2 size={13} /> Eliminar</button>
    </div>
  </article>
);

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("todas");
  const [tab, setTab] = useState("solicitudes");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, authHeaders());
      setItems(Array.isArray(data) ? data : []);
      setAuthed(true);
    } catch {
      setAuthed(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    await axios.patch(`${API}/admin/bookings/${id}`, { status }, authHeaders());
    setItems((p) => p.map((b) => (b.id === id ? { ...b, status } : b)));
  };
  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta solicitud?")) return;
    await axios.delete(`${API}/admin/bookings/${id}`, authHeaders());
    setItems((p) => p.filter((b) => b.id !== id));
  };
  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setAuthed(false);
  };

  if (authed === null) return <div className="min-h-screen bg-jungle" />;
  if (!authed) return <Login onOk={load} />;

  const shown = filter === "todas" ? items : items.filter((b) => b.status === filter);
  return (
    <div className="min-h-screen bg-paper text-jungle" data-testid="admin-dashboard">
      <header className="bg-jungle text-paper">
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3"><img src="/sello-gato-sm.jpg" alt="" className="h-10 w-10 rounded-lg object-cover" /><span className="font-display text-xl">Panel</span></a>
          <div className="flex gap-2">
            <button onClick={load} data-testid="admin-refresh" className="rounded-full border border-paper/30 p-2.5" aria-label="Recargar"><RefreshCw size={16} /></button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-paper/30 px-4 py-2 text-xs font-bold uppercase"><LogOut size={14} /> Salir</button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-10">
        <div className="mb-8 inline-flex rounded-full bg-jungle/10 p-1" data-testid="admin-tabs">
          {[["solicitudes", "Solicitudes"], ["galeria", "Galería"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`rounded-full px-5 py-2 text-xs font-bold uppercase tracking-wider ${tab === k ? "bg-jungle text-paper" : "text-jungle"}`}>{l}</button>
          ))}
        </div>
        {tab === "galeria" ? <AdminGallery /> : (<>
        <div className="flex flex-wrap gap-2" data-testid="admin-filters">
          {["todas", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`} className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider border ${filter === s ? "bg-jungle text-paper border-jungle" : "border-jungle/20"}`}>
              {s} ({s === "todas" ? items.length : items.filter((b) => b.status === s).length})
            </button>
          ))}
        </div>
        <div className="mt-8 flex flex-col gap-4">
          {shown.length === 0 && <p className="text-jungle/70" data-testid="admin-empty">No hay solicitudes.</p>}
          {shown.map((b) => <Row key={b.id} b={b} onStatus={onStatus} onDelete={onDelete} />)}
        </div>
        </>)}
      </main>
    </div>
  );
}
